import { cn } from '@commenda/utils';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, VariantProps } from 'class-variance-authority';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';

const checkboxVariants = cva(
  [
    'peer shrink-0 flex items-center justify-center transition',
    'outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fg-brand',
    'border border-border-primary disabled:cursor-not-allowed disabled:border-border-disabled',
    // group-hover mirrors hover so the checkbox reflects a hovered ancestor `.group` (e.g. a DataTable row).
    'hover:data-[state=unchecked]:border-border-primary-hover group-hover:data-[state=unchecked]:border-border-primary-hover',
    'data-[state=checked]:bg-fg-brand data-[state=indeterminate]:bg-fg-brand',
    'data-[state=checked]:border-fg-brand data-[state=indeterminate]:border-fg-brand',
    'data-[state=checked]:text-fg-white data-[state=indeterminate]:text-fg-white',
    'hover:data-[state=checked]:bg-fg-brand-hover hover:data-[state=indeterminate]:bg-fg-brand-hover group-hover:data-[state=checked]:bg-fg-brand-hover group-hover:data-[state=indeterminate]:bg-fg-brand-hover',
    'disabled:data-[state=checked]:bg-bg-disabled-subtle disabled:data-[state=indeterminate]:bg-bg-disabled-subtle',
    'disabled:data-[state=checked]:text-fg-disabled disabled:data-[state=indeterminate]:text-fg-disabled',
    // A disabled checkbox is neutral, not brand-blue, even when checked/indeterminate.
    'disabled:data-[state=checked]:border-border-disabled disabled:data-[state=indeterminate]:border-border-disabled',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'h-4 w-4 rounded-xs',
        lg: 'h-5 w-5 rounded-sm',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

const checkboxIndicatorVariants = cva(
  'flex items-center justify-center text-current',
  {
    variants: {
      size: {
        sm: 'h-3 w-3',
        lg: 'h-4 w-4',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  /**
   * @deprecated Pass the tri-state `checked="indeterminate"` instead — it is the
   * single source of truth for the indicator, background, and glyph. Retained so
   * callers that still set this alongside a boolean `checked` keep working: a
   * truthy value coerces the state to indeterminate.
   */
  indeterminate?: boolean;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size, indeterminate, checked, ...props }, ref) => {
  // One source of truth: the deprecated `indeterminate` boolean only coerces the
  // tri-state. Deriving Radix's state (indicator visibility + background) and the
  // glyph from the same value stops them drifting out of sync — the footgun where
  // `checked={false} indeterminate` rendered nothing at all.
  const state = indeterminate ? 'indeterminate' : checked;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={state}
      className={cn(checkboxVariants({ size, className }))}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn(checkboxIndicatorVariants({ size }))}
      >
        {state === 'indeterminate' ? (
          // strokeWidth=3 matches the Check icon's visual weight at 12-16px.
          <Minus className="h-full w-full" strokeWidth={3} />
        ) : (
          <Check className="h-full w-full" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export default Checkbox;
